<template>
  <div ref="observerElement">
    <div v-if="!showError">
      <video
        ref="videoElement"
        class="video-js"
        preload="auto"
        muted
      >
        <source
          :src="source"
          type="application/x-mpegURL"
        >
      </video>
    </div>
    <div
      v-else
      class="error-container"
    >
      <div class="camera-offline-icon">
        <feather-icon
          icon="VideoOffIcon"
          size="48"
          class="error-icon"
        />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import videojs from 'video.js'

// eslint-disable-next-line import/no-extraneous-dependencies
require('video.js/dist/video-js.css')

export default {
  components: {
  },
  props: {
    source: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      observer: null,
      showError: false,
      player: null,
      retryTimeout: null,
      errorRetryCount: 0,
      maxRetries: 3,
      isDisposed: false,
    }
  },
  watch: {
    source(newSource) {
      if (this.player && newSource) {
        this.showError = false
        this.errorRetryCount = 0
        this.player.src({
          src: newSource,
          type: 'application/x-mpegURL',
        })
        this.player.load()
        this.player.play().catch(err => {
          console.warn('Video playback was prevented:', err)
        })
      }
    },
  },
  mounted() {
    this.initializePlayer()
    this.setupIntersectionObserver()
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }
  },
  methods: {
    initializePlayer() {
      try {
        this.player = videojs(this.$refs.videoElement, {
          html5: {
            hls: {
              smoothQualityChange: true,
              maxBufferLength: 30,
              maxBufferSize: 60 * 1000 * 1000, // 60MB
              overrideNative: true, // Force HTML5 player for HLS
              enableLowInitialPlaylist: true, // Start with lower quality
            },
          },
          liveui: true, // Enable live UI for streaming
          errorDisplay: false, // Disable default error display
        }, () => {
          this.player.on('error', this.handleError)

          // Add event listener for successful loading
          this.player.on('loadedmetadata', () => {
            this.errorRetryCount = 0 // Reset retry counter on successful load
            this.showError = false
          })
        })

        // ลองเล่นวิดีโอ
        this.player.play().catch(err => {
          console.warn('Initial video playback was prevented:', err)
        })
      } catch (error) {
        console.error('Error initializing video player:', error)
        this.showError = true
      }
    },
    handleError() {
      const error = this.player ? this.player.error() : null
      const errorCode = error ? error.code : 0

      if (error) {
        console.warn(`Video error (${errorCode}): ${error.message}`)
      }

      // ลองโหลดใหม่ถ้ายังไม่เกินจำนวนครั้งที่กำหนด
      if (this.errorRetryCount < this.maxRetries) {
        this.errorRetryCount += 1
        console.log(`Retrying video load (${this.errorRetryCount}/${this.maxRetries})...`)

        // ล้าง timeout เดิมถ้ามี
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout)
        }

        // Exponential backoff for retries: 2s, 4s, 8s
        const retryDelay = 2 ** this.errorRetryCount * 1000

        // ลองโหลดใหม่
        this.retryTimeout = setTimeout(() => {
          if (this.isDisposed) return // Don't retry if already disposed

          if (!this.player || this.player.isDisposed()) {
            this.isDisposed = false
            this.initializePlayer()
          } else {
            // Reset the player and try again
            this.player.reset()
            this.player.src({ type: 'application/x-mpegURL', src: this.source })
            this.player.load()

            // Try to play but handle rejection
            try {
              const playPromise = this.player.play()
              if (playPromise !== undefined) {
                playPromise.catch(playError => console.log('Play error during retry:', playError))
              }
            } catch (e) {
              console.log('Exception during retry play:', e)
            }
          }
        }, retryDelay)
      } else {
        // เกินจำนวนครั้งที่กำหนด แสดงข้อผิดพลาด
        console.log('Max retries exceeded for', this.source)
        this.showError = true
        this.$emit('video-error', this.index)
      }
    },
    setupIntersectionObserver() {
      const options = {
        root: null, // observing with respect to viewport
        threshold: 0.5, // trigger if 50% of the player is visible
      }
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.player && !this.showError) {
            try {
              const playPromise = this.player.play()
              // ตรวจสอบว่า play() คืนค่า Promise หรือไม่
              if (playPromise !== undefined) {
                playPromise.catch(err => {
                  console.warn('Intersection observer playback was prevented:', err)
                })
              }
            } catch (error) {
              console.warn('Error during play:', error)
            }
          } else if (this.player) {
            this.player.pause()
          }
        })
      }, options)
      this.observer.observe(this.$refs.observerElement)
    },
  },
}
</script>

<style scoped>
.video-js {
  width: 100%; /* Make the video fill the width of its parent */
  height: 0; /* Set height to 0 initially */
  padding-bottom: 56.25%; /* 16:9 aspect ratio (9/16 = 0.5625) */
  position: relative;
  overflow: hidden;
}

.video-js video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* Cover the container while maintaining aspect ratio */
}

.error-container {
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  position: relative;
  background-color: #000000;
  overflow: hidden;
}

.camera-offline-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: transparent !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.error-icon {
  color: #ffa600; /* ปรับสีไอคอนเป็นสีแดงสด */
  stroke-width: 1.5; /* เพิ่มความหนาของเส้นไอคอนให้เห็นชัดขึ้น */
}
</style>
